import { posts as postsApi, getCachedUser, mediaUrl } from '../api.js';
import { navigate } from '../utils/router.js';
import {
  esc,
  avatarUrl,
  formatTime,
  toast,
  copyText,
  postShareUrl,
  badgeHtml,
} from '../utils/dom.js';
import { openCompose } from './compose.js';

function normalizeList(res) {
  if (!res) return { items: [], cursor: null };
  if (Array.isArray(res)) return { items: res, cursor: null };
  const items = res.items || res.posts || res.data || [];
  const cursor = res.nextCursor || res.cursor || res.next || null;
  return { items, cursor };
}

function postAuthor(p) {
  const a = p.author || p.user || null;
  if (a) return a;
  return {
    gid: p.gid || p.userGid || p.authorGid,
    nickname: p.nickname || p.authorName || '用户',
    avatar_url: p.avatar_url || p.avatar,
    is_verified: p.is_verified,
    is_student: p.is_student,
    is_teacher: p.is_teacher,
    is_gold: p.is_gold,
  };
}

function postImage(p) {
  const raw = p.image_url || p.imageUrl || p.image || (p.images && p.images[0]);
  return raw ? mediaUrl(raw) : '';
}

export function renderPostCard(p, { currentUser, onChange } = {}) {
  const author = postAuthor(p);
  const me = currentUser || getCachedUser();
  const isMine =
    me &&
    (String(author.gid) === String(me.gid) ||
      String(author.id) === String(me.id) ||
      String(p.user_id) === String(me.id));
  const liked = !!(p.liked || p.isLiked || p.hasLiked);
  const likeCount = p.like_count ?? p.likeCount ?? p.likes ?? 0;
  const commentCount = p.comment_count ?? p.commentCount ?? p.comments ?? 0;
  const image = postImage(p);
  const id = p.id || p._id;

  const div = document.createElement('article');
  div.className = 'post-card';
  div.dataset.postId = id;
  div.innerHTML = `
    <img class="post-avatar" src="${esc(avatarUrl(author))}" alt="" data-gid="${esc(author.gid || '')}" />
    <div class="post-body">
      <div class="post-header">
        <span class="name" data-gid="${esc(author.gid || '')}">${esc(author.nickname || '用户')}${badgeHtml(
          author
        )}</span>
        <span class="meta">GID ${esc(author.gid ?? '')} · ${esc(
          formatTime(p.created_at || p.createdAt || p.time)
        )}</span>
        ${
          isMine
            ? `<div class="post-menu-wrap">
                <button type="button" class="post-menu-btn" data-menu aria-label="更多">⋮</button>
                <div class="post-menu hidden" data-menu-panel>
                  <button type="button" data-edit>编辑</button>
                  <button type="button" class="danger" data-delete>删除</button>
                </div>
              </div>`
            : ''
        }
      </div>
      <div class="post-content">${esc(p.content || p.text || '')}</div>
      ${
        image
          ? `<div class="post-image"><img src="${esc(image)}" alt="配图" data-lightbox /></div>`
          : ''
      }
      <div class="post-actions">
        <button type="button" class="post-action ${liked ? 'liked' : ''}" data-like>
          <span>${liked ? '♥' : '♡'}</span>
          <span data-like-count>${likeCount}</span>
        </button>
        <button type="button" class="post-action" data-comment>
          <span>💬</span>
          <span data-comment-count>${commentCount}</span>
        </button>
        <button type="button" class="post-action" data-share>
          <span>↗</span>
          <span>分享</span>
        </button>
      </div>
      <div class="comments-panel hidden" data-comments></div>
    </div>
  `;

  div.querySelectorAll('[data-gid]').forEach((node) => {
    node.addEventListener('click', () => {
      const g = node.getAttribute('data-gid');
      if (g) navigate(`/user/${g}`);
    });
  });

  div.querySelector('[data-lightbox]')?.addEventListener('click', (e) => {
    e.stopPropagation();
    openLightbox(image);
  });

  const menuBtn = div.querySelector('[data-menu]');
  const menuPanel = div.querySelector('[data-menu-panel]');
  menuBtn?.addEventListener('click', (e) => {
    e.stopPropagation();
    menuPanel?.classList.toggle('hidden');
  });

  div.querySelector('[data-edit]')?.addEventListener('click', () => {
    openCompose({
      editPost: {
        id,
        content: p.content || p.text || '',
        image_url: p.image_url || p.imageUrl || '',
      },
      onDone: () => onChange?.(),
    });
  });

  div.querySelector('[data-delete]')?.addEventListener('click', async () => {
    if (!confirm('确定删除这条帖子？')) return;
    try {
      await postsApi.remove(id);
      toast('已删除', 'success');
      div.remove();
      onChange?.();
    } catch (err) {
      toast(err.message || '删除失败', 'error');
    }
  });

  div.querySelector('[data-like]')?.addEventListener('click', async () => {
    const btn = div.querySelector('[data-like]');
    const countEl = div.querySelector('[data-like-count]');
    const wasLiked = btn.classList.contains('liked');
    try {
      if (wasLiked) {
        await postsApi.unlike(id);
        btn.classList.remove('liked');
        btn.querySelector('span').textContent = '♡';
        countEl.textContent = String(Math.max(0, Number(countEl.textContent) - 1));
      } else {
        await postsApi.like(id);
        btn.classList.add('liked');
        btn.querySelector('span').textContent = '♥';
        countEl.textContent = String(Number(countEl.textContent) + 1);
      }
    } catch (err) {
      toast(err.message || '操作失败', 'error');
    }
  });

  div.querySelector('[data-share]')?.addEventListener('click', async () => {
    const url = postShareUrl(id);
    try {
      await copyText(url);
      toast('链接已复制', 'success');
    } catch {
      toast(url, 'info');
    }
  });

  div.querySelector('[data-comment]')?.addEventListener('click', async () => {
    const panel = div.querySelector('[data-comments]');
    if (!panel.classList.contains('hidden') && panel.dataset.loaded) {
      panel.classList.toggle('hidden');
      return;
    }
    panel.classList.remove('hidden');
    panel.innerHTML = `<div class="center-box"><div class="spinner"></div></div>`;
    try {
      const res = await postsApi.comments(id);
      const { items } = normalizeList(res);
      panel.dataset.loaded = '1';
      panel.innerHTML = `
        <div data-comment-list>
          ${
            items.length
              ? items
                  .map((c) => {
                    const u = c.author || c.user || {};
                    return `<div class="comment-item">
                      <img src="${esc(avatarUrl(u))}" alt="" />
                      <div>
                        <strong>${esc(u.nickname || '用户')}</strong>
                        <span class="faint text-xs"> · ${esc(
                          formatTime(c.created_at || c.createdAt)
                        )}</span>
                        <div>${esc(c.content || c.text || '')}</div>
                      </div>
                    </div>`;
                  })
                  .join('')
              : `<div class="text-sm muted">暂无评论</div>`
          }
        </div>
        <form class="comment-form" data-comment-form>
          <input type="text" placeholder="写下评论…" maxlength="500" required />
          <button type="submit" class="btn btn-primary btn-sm">发送</button>
        </form>
      `;
      panel.querySelector('[data-comment-form]')?.addEventListener('submit', async (e) => {
        e.preventDefault();
        const input = e.target.querySelector('input');
        const text = input.value.trim();
        if (!text) return;
        try {
          await postsApi.addComment(id, text);
          input.value = '';
          panel.dataset.loaded = '';
          div.querySelector('[data-comment]').click();
          const cc = div.querySelector('[data-comment-count]');
          cc.textContent = String(Number(cc.textContent || 0) + 1);
        } catch (err) {
          toast(err.message || '评论失败', 'error');
        }
      });
    } catch (err) {
      panel.innerHTML = `<div class="text-sm" style="color:var(--danger)">${esc(
        err.message || '加载失败'
      )}</div>`;
    }
  });

  return div;
}

function openLightbox(src) {
  const lb = document.createElement('div');
  lb.className = 'lightbox';
  lb.innerHTML = `<img src="${esc(src)}" alt="" />`;
  lb.addEventListener('click', () => lb.remove());
  document.body.appendChild(lb);
}

export async function renderFeed(container, { currentUser } = {}) {
  container.innerHTML = `<div class="center-box"><div class="spinner"></div></div>`;
  try {
    const res = await postsApi.feed({ limit: 30 });
    const { items, cursor } = normalizeList(res);
    container.innerHTML = '';
    const list = document.createElement('div');
    list.className = 'post-list';
    if (!items.length) {
      list.innerHTML = `<div class="empty-state"><div>🌌</div><p>还没有帖子，成为第一个发声的人吧</p></div>`;
    } else {
      items.forEach((p) => {
        list.appendChild(
          renderPostCard(p, {
            currentUser,
            onChange: () => renderFeed(container, { currentUser }),
          })
        );
      });
    }
    container.appendChild(list);

    if (cursor) {
      let next = cursor;
      const more = document.createElement('div');
      more.className = 'center-box';
      more.innerHTML = `<button type="button" class="btn btn-ghost" data-more>加载更多</button>`;
      more.querySelector('[data-more]').addEventListener('click', async () => {
        const btn = more.querySelector('[data-more]');
        btn.disabled = true;
        try {
          const r2 = await postsApi.feed({ cursor: next, limit: 30 });
          const n = normalizeList(r2);
          n.items.forEach((p) =>
            list.appendChild(
              renderPostCard(p, {
                currentUser,
                onChange: () => renderFeed(container, { currentUser }),
              })
            )
          );
          if (n.cursor) {
            next = n.cursor;
            btn.disabled = false;
          } else {
            more.remove();
          }
        } catch (err) {
          toast(err.message || '加载失败', 'error');
          btn.disabled = false;
        }
      });
      container.appendChild(more);
    }
  } catch (err) {
    container.innerHTML = `<div class="empty-state"><p style="color:var(--danger)">${esc(
      err.message || '加载时间线失败'
    )}</p>
      <button type="button" class="btn btn-ghost btn-sm" id="feed-retry">重试</button></div>`;
    container.querySelector('#feed-retry')?.addEventListener('click', () =>
      renderFeed(container, { currentUser })
    );
  }
}

export async function renderSinglePost(container, postId, { currentUser } = {}) {
  container.innerHTML = `<div class="center-box"><div class="spinner"></div></div>`;
  try {
    const p = await postsApi.get(postId);
    const post = p.post || p.data || p;
    container.innerHTML = '';
    const list = document.createElement('div');
    list.className = 'post-list';
    list.appendChild(
      renderPostCard(post, {
        currentUser,
        onChange: () => renderSinglePost(container, postId, { currentUser }),
      })
    );
    container.appendChild(list);
    list.querySelector('[data-comment]')?.click();
  } catch (err) {
    container.innerHTML = `<div class="empty-state"><p style="color:var(--danger)">${esc(
      err.message || '帖子不存在'
    )}</p></div>`;
  }
}
